












































import { computed, defineComponent, onUnmounted, PropType, ref, watch } from "@vue/composition-api";
import { CollabLiveChat } from "@scrile/api-provider/dist/api/CollabLiveChatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useDoNotDisturb from "@/hooks/useDoNotDisturb";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { parseISO } from "@/lib/time";
import { getLiveUrl } from "@/lib/urlHelpers";
import BaseIconButton from "@/components/BaseIconButton";
import BaseMenu from "@/components/BaseMenu";
import StreamView from "../../../components/StreamView";
import { sendJoinNotification } from "../../../helpers";
import useConsume from "../../../hooks/useConsume";
import useProduce from "../../../hooks/useProduce";
import { StreamUser } from "../../../types";
import useCollabLiveChat from "../../hooks/useCollabLiveChat";
import CollabLiveSettings from "../CollabLiveSettings";
import LeaveCollabModal from "../LeaveCollabModal";

export default defineComponent({
  name: "ProduceController",
  components: { LeaveCollabModal, BaseIconButton, BaseMenu, CollabLiveSettings, StreamView },
  props: {
    collabLive: { type: Object as PropType<CollabLiveChat>, required: true },
    user: { type: Object as () => UserPublicData, required: true },
  },
  setup(props, { root, emit }) {
    const { setDoNotDisturb } = useDoNotDisturb();
    const { authUser } = useAuthUser();

    const showCollabSettings = ref(false);
    const showLeaveCollab = ref(false);

    const {
      loading,
      joinData,
      userList,
      referrersMap,
      clientServerTimeDiff,
      errorData,
      usersAmountByReferrer,
      endEntireCollabLive,
    } = useCollabLiveChat({
      chatId: props.collabLive.id,
      userId: props.user.id,
    });

    const getLoggerContext = (): Record<string, any> => {
      return {
        joinData: joinData.value,
        errorData: errorData.value,
      };
    };

    const {
      stream,
      connecting,
      producerSpeaking,
      connectingState,
      constraints,
      produceStream,
      onChangeDevice,
      initStream,
    } = useProduce(getLoggerContext);

    const { usersToConsume, consumeStream, removeUser } = useConsume({ joinData, getLoggerContext });

    const isOwner = computed(() => props.collabLive.ownerId === authUser.value?.id);
    const dateStart = computed(() => {
      const data = joinData.value;
      const timeDiff = clientServerTimeDiff.value;
      if (!data) {
        return "";
      }
      return new Date(parseISO(data.me.joinTime).getTime() + timeDiff).toISOString();
    });
    const streamers = computed<StreamUser[]>(() => {
      return [
        {
          user: joinData.value?.me,
          stream: stream.value,
          constraints: constraints.value,
          isSpeaking: producerSpeaking.value,
        },
        ...usersToConsume.value,
      ];
    });
    const menuItems = computed(() =>
      isOwner.value
        ? [
            {
              name: "collabSettings",
              text: $t("Collab settings"),
            },
            {
              name: "endChat",
              text: $t("End live"),
              class: "negative",
            },
          ]
        : []
    );
    const handleBlockForSession = async ({
      chatUserId,
      cb,
      loadingCb,
    }: {
      chatUserId: string;
      cb: () => void;
      loadingCb: () => void;
    }) => {
      try {
        await providers.LivechatsProvider.userUpdate({
          data: { chatUserId, blocked: true },
        });
        cb();
      } finally {
        loadingCb();
      }
    };

    const goSingleLive = async () => {
      if (!authUser.value) return;
      root.$router.replace(getLiveUrl(authUser.value));
    };

    const endCollab = async (cb: () => void) => {
      if (!authUser.value) return;
      if (isOwner.value) {
        await endEntireCollabLive();
        cb();
      } else {
        emit("close");
      }
    };

    watch(joinData, (joinData, old) => {
      if (!old && joinData) {
        produceStream(joinData.token);
      }
    });

    watch(joinData, (joinData, old) => {
      if (!joinData) return;
      const streamingUsers = joinData.users.filter(i => i.broadcasting && i.id !== joinData.me.id);
      const streamingUserIds = streamingUsers.map(i => i.id);
      const oldStreamingUserIds = (old?.users.filter(i => i.broadcasting && i.id !== old.me.id) ?? []).map(i => i.id);

      const idsToConsume = streamingUserIds.filter(id => !oldStreamingUserIds.includes(id));
      idsToConsume.forEach(id => {
        consumeStream(id, joinData.token);
        const user = streamingUsers.find(i => i.id === id);
        old && user?.user && sendJoinNotification(user?.user);
      });
      const idsToRemove = oldStreamingUserIds.filter(id => !streamingUserIds.includes(id));
      idsToRemove.forEach(id => removeUser(id));
    });

    initStream();
    setDoNotDisturb(true);

    onUnmounted(() => {
      setDoNotDisturb(false);
    });

    return {
      isOwner,
      connecting,
      connectingState,
      loading,
      userList,
      dateStart,
      streamers,
      constraints,
      stream,
      joinData,
      showCollabSettings,
      showLeaveCollab,
      menuItems,
      referrersMap,
      usersAmountByReferrer,
      onChangeDevice,
      goSingleLive,
      endCollab,
      handleBlockForSession,
    };
  },
});
